import { useEffect } from 'react';
import {
  Alert,
  Button,
  Checkbox,
  Flex,
  Form,
  Input,
  Modal,
  Select,
} from 'antd';
import { useMutation } from '@apollo/client/react';
import dayjs from 'dayjs';

import DatePicker from 'components/DatePicker/DatePicker';
import SectionMessage from 'components/SectionMessage/SectionMessage';

import { UPDATE_BLOCKED_PHONE_NUMBER } from 'mutations/blockPhone';
import { REASON_CODES } from './constants';

import useTimeoutValue from 'hooks/useTimeoutValue';
import { SIZE, STATUS } from 'utilities/constants';
import { formatDateToSubmit } from 'utilities/datesAndTimes';

const { TextArea } = Input;

const EditBlockedPhoneModal = ({
  hide,
  record,
  onEditComplete,
  ...modalProps
}) => {
  const [form] = Form.useForm();
  const [editSuccess, setEditSuccess] = useTimeoutValue(false, 4000);
  const [unblockSuccess, setUnblockSuccess] = useTimeoutValue(false, 4000);

  Form.useWatch([], form);
  const blockPermanently = form.getFieldValue('blockPermanently');

  const [updateBlockedPhoneNumber, updateMutation] = useMutation(
    UPDATE_BLOCKED_PHONE_NUMBER,
    {
      onCompleted: () => {
        setTimeout(() => {
          if (modalProps.open) {
            hide();
          }
        }, 4000);
      },
      refetchQueries: ['BlockedPhoneNumberHistory'],
    },
  );

  useEffect(() => {
    if (modalProps.open && record) {
      form.setFieldsValue({
        blockEnd: record.blockEnd ? dayjs.utc(record.blockEnd) : null,
        blockPermanently: false,
        reasonCode: record.reasonCode,
        reasonDescription: record.reasonDescription,
      });
    }
  }, [modalProps.open, record, form]);

  const handleBlockPermanentlyChange = (e) => {
    if (e.target.checked) {
      form.setFieldValue('blockEnd', null);
    }
  };

  const handleSubmit = async (values) => {
    const { blockEnd, reasonCode, reasonDescription } = values;

    await updateBlockedPhoneNumber({
      variables: {
        id: record.id,
        blockEnd: blockEnd ? formatDateToSubmit(dayjs(blockEnd), true) : null,
        reasonCode,
        reasonDescription,
      },
    });
    setEditSuccess(true);
    if (onEditComplete) {
      onEditComplete();
    }
  };

  const handleUnblock = async () => {
    await updateBlockedPhoneNumber({
      variables: {
        id: record.id,
        unblock: true,
      },
    });
    setUnblockSuccess(true);
    if (onEditComplete) {
      onEditComplete();
    }
  };

  const handleCancel = () => {
    form.resetFields();
    updateMutation.reset();
    hide();
  };

  const canUnblock = record?.isBlocked;
  const isSubmitting = updateMutation.loading || editSuccess || unblockSuccess;
  const hasFieldsChanged = form.isFieldsTouched();

  return (
    <Modal
      {...modalProps}
      onCancel={handleCancel}
      title="Edit Blocked Phone Number"
      footer={
        <Flex justify="space-between">
          <Button
            danger
            onClick={handleUnblock}
            disabled={!canUnblock || isSubmitting}
            loading={updateMutation.loading}
            data-testid="unblock-phone-button"
          >
            Unblock
          </Button>
          <Flex gap="small">
            <Button onClick={handleCancel} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button
              type="primary"
              onClick={form.submit}
              loading={updateMutation.loading}
              data-testid="save-changes-button"
              disabled={isSubmitting || !hasFieldsChanged}
            >
              Save Changes
            </Button>
          </Flex>
        </Flex>
      }
    >
      <div style={{ position: 'relative' }}>
        {editSuccess && (
          <SectionMessage
            size={SIZE.SM}
            status={STATUS.SUCCESS}
            text={
              blockPermanently
                ? 'Updated to block permanently'
                : 'Blocked phone number updated'
            }
            cover={true}
            visible={editSuccess}
          />
        )}
        {unblockSuccess && (
          <SectionMessage
            size={SIZE.SM}
            status={STATUS.SUCCESS}
            text="Phone number unblocked"
            cover={true}
            visible={unblockSuccess}
          />
        )}

        <Form form={form} onFinish={handleSubmit} layout="vertical">
          <Form.Item label="Block End Date" name="blockEnd">
            <DatePicker
              style={{ width: '100%' }}
              disabled={blockPermanently}
              disabledDate={(current) => current < dayjs().startOf('day')}
            />
          </Form.Item>
          {record?.blockEnd && (
            <Form.Item
              name="blockPermanently"
              valuePropName="checked"
              style={{ marginTop: '-12px' }}
            >
              <Checkbox onChange={handleBlockPermanentlyChange}>
                Block Permanently
              </Checkbox>
            </Form.Item>
          )}
          <Form.Item label="Reason Code" name="reasonCode">
            <Select placeholder="Select a reason" options={REASON_CODES} />
          </Form.Item>
          <Form.Item label="Reason Description" name="reasonDescription">
            <TextArea
              rows={3}
              placeholder="Provide a detailed reason for blocking this number"
            />
          </Form.Item>
        </Form>

        {updateMutation.error && (
          <Alert
            banner
            type="error"
            title="Failed to update blocked phone number. Please try again."
          />
        )}
      </div>
    </Modal>
  );
};

export default EditBlockedPhoneModal;
